<template>
  <div class="editPharmacy">
    <Loading v-if="loading" />
    <Title :title="$t('pharmacyInformation')" />
    <FormKit
      type="form"
      id="pharmacyForm"
      v-model="formData"
      :actions="false"
      @submit="submitHandler"
    >
      <Input
        v-for="el in infoOfPharmacy"
        :key="el.id"
        :type="el.type"
        :name="el.name"
        :label="$t(el.label)"
        :validation="el.validation"
        :validationMessages="{
          required: $t('validation.required')
        }"
        :disabled="disabledInput(el.name)"
        :data-testid="el.dataTestId"
      />
      <Dropdown
        class="infoOfPharmacy-dropdown"
        :selectedOption="langsDropdown.selectedOption"
        :options="langsDropdown.options"
        :label="$t('chooseLanguage')"
        :multiple="true"
        @selectOption="selectLanguage"
      />
      <div class="block-input" v-for="el in infoOfDeliveries" :key="el.id">
        <Input
          :type="el.type"
          :name="el.name"
          :label="$t(el.label)"
          :disabled="disabledInput(el.name)"
          :data-testid="el.dataTestId"
        />
        <span>
          {{ $t('daysDelivery') }}
        </span>
      </div>
      <Input
        v-for="el in infoEmail"
        :key="el.id"
        :type="el.type"
        :name="el.name"
        :label="$t(el.label)"
        :validation="el.validation"
        :validationMessages="{
          required: $t('validation.required'),
          email: $t('validation.email')
        }"
        :data-testid="el.dataTestId"
      />
      <FormKit type="group" name="additionalEmails">
        <Input
          v-for="(el, idx) in emailItems"
          :key="el"
          :name="el"
          :id="el"
          :label="$t('alarmEmail')"
          :validation="'required|email'"
          :validationMessages="{
            required: $t('validation.required'),
            email: $t('validation.email')
          }"
          :disabled="!user?.role.permissions.pharmacy.update"
          @closeInput="removeEmailItem"
          :data-testid="'inputAddAlarmEmail' + idx"
        />
      </FormKit>
      <AddInfo :title="$t('email')" @click="addEmailItem" />
      <Input
        v-for="el in infoPhoneNumber"
        :key="el.id"
        :type="el.type"
        :name="el.name"
        :label="$t(el.label)"
        :validation="el.validation"
        :validation-visibility="el.validationVisibility"
        :validationMessages="{
          matches: $t('validation.phoneNumber'),
          required: $t('validation.required')
        }"
        :disabled="!user?.role.permissions.pharmacy.update"
        :data-testid="el.dataTestId"
      />
      <FormKit type="group" name="additionalPhoneNumbers">
        <Input
          v-for="(el, idx) in phoneItems"
          :key="el"
          :name="el"
          :id="el"
          :label="$t('alarmPhoneNumber')"
          :validation="[['required'], ['matches', /^\+[0-9]{10,14}$/]]"
          validation-visibility="dirty"
          :validationMessages="{
            matches: $t('validation.phoneNumber'),
            required: $t('validation.required')
          }"
          :disabled="!user?.role.permissions.pharmacy.update"
          @closeInput="removePhoneItem"
          :data-testid="'inputAddAlarmPhoneNumber' + idx"
        />
      </FormKit>
      <AddInfo :title="$t('phoneNumber')" @click="addPhoneItem" />
      <Input
        type="number"
        name="shelvesNumber"
        :label="$t('shelvesNumber')"
        validation="required"
        :validationMessages="{
          required: $t('validation.required')
        }"
      />
    </FormKit>
  </div>
  <Footer
    :disabledMakeServiceRequest="formData.serviceRequest"
    @cleanForm="cleanForm"
    @submitForm="submitForm"
    @openModalServiceRequest="openModal"
  />
  <ServiceRequest
    :showModalServiceRequest="showModalServiceRequest"
    @closeModal="showModalServiceRequest = false"
    @saveServiceRequest="saveServiceRequest"
  />
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';
import { token } from '@formkit/utils';

import Loading from '@/views/Loading.vue';
import ServiceRequest from './ServiceRequest/ServiceRequest.vue';

import {
  defaultData,
  infoOfPharmacy,
  infoPhoneNumber,
  infoEmail,
  infoOfDeliveries
} from './configs';
import {
  optionsLanguageEn,
  optionsLanguageFi,
  optionsLanguageSv,
  optionsLanguageNo,
  langsEn,
  langsFi,
  langsSv,
  langsNo
} from '@/services/languageService';

import { Option } from '@/types';
import { DataServiceRequest } from '@/store/modules/pharmacy/types';

@Options({
  name: 'EditPharmacy',
  emits: ['saveServiceRequest'],
  components: {
    Loading,
    ServiceRequest
  },
  data() {
    return {
      formData: defaultData,
      payload: null,
      emailItems: [],
      phoneItems: [],
      infoOfPharmacy,
      infoPhoneNumber,
      infoEmail,
      infoOfDeliveries,
      langsEn,
      langsFi,
      langsSv,
      langsNo,
      langsDropdown: {
        selectedOption: [],
        options: []
      },
      loading: false,
      showModalServiceRequest: false
    };
  },
  created() {
    this.show();
  },
  computed: {
    ...mapState('pharmacy', ['pharmacy']),
    ...mapState('account', ['user'])
  },
  methods: {
    ...mapActions('pharmacy', [
      'getPharmacy',
      'getPharmacyServiceRequest',
      'updatePharmacy'
    ]),
    async show() {
      const { id } = this.$route.params;
      this.loading = true;

      await this.getPharmacy(id);
      this.fillForm();

      this.loading = false;
    },
    initLanguage() {
      const optionsMappings: { [key: string]: Option[] } = {
        en: optionsLanguageEn,
        fi: optionsLanguageFi,
        sv: optionsLanguageSv,
        no: optionsLanguageNo
      };

      const userLanguage = this.user?.appLanguage;
      if (userLanguage && optionsMappings[userLanguage]) {
        this.langsDropdown.options = optionsMappings[userLanguage];
      }
    },
    fillAdditionalFields(items: string[], field: string, itemName: string) {
      const newFieldData: Record<string, string> = {};

      this.pharmacy[field]?.forEach((value: string) => {
        const tokenKey = `${itemName}${token()}`;
        items.push(tokenKey);
        newFieldData[tokenKey] = value;
      });

      this.formData[field] = newFieldData;
    },
    fillForm() {
      Object.keys(this.pharmacy).forEach((field) => {
        switch (field) {
          case 'additionalEmails': {
            this.formData.additionalEmails = {};
            this.emailItems = [];
            this.fillAdditionalFields(this.emailItems, field, 'email');
            break;
          }
          case 'additionalPhoneNumbers': {
            this.formData.additionalPhoneNumbers = {};
            this.phoneItems = [];
            this.fillAdditionalFields(this.phoneItems, field, 'phone');
            break;
          }
          default: {
            this.formData[field] = this.pharmacy[field];
            break;
          }
        }
      });
    },
    disabledInput(name: string) {
      if (
        name === 'pinCode' ||
        name === 'deliveryKeepTime' ||
        name === 'deliveryReminderMessagePeriod' ||
        !this.user?.role.permissions.pharmacy.update
      ) {
        return true;
      } else {
        false;
      }
    },
    // eslint-disable-next-line
    async submitHandler(data: any) {
      this.loading = true;
      const { id } = this.$route.params;

      this.payload = {
        name: data.name,
        email: data.email,
        customerSMSLanguages: data.customerSMSLanguages,
        phoneNumber: data.phoneNumber,
        serviceRequest: data.serviceRequest,
        deliveryKeepTime: data.deliveryKeepTime,
        deliveryReminderMessagePeriod: data.deliveryReminderMessagePeriod,
        pinCode: data.pinCode,
        strongAuthentication: data.strongAuthentication,
        pickUpMachine: data.pickUpMachine,
        zipCode: data.zipCode,
        streetAddress: data.streetAddress,
        country: data.country,
        city: data.city,
        businessId: data.businessId,
        additionalEmails: Object.values(data.additionalEmails),
        additionalPhoneNumbers: Object.values(data.additionalPhoneNumbers),
        pickupBox: {
          ...data.pickupBox,
          customerSMSLanguages: data.customerSMSLanguages
        }
      };

      if (data.shelvesNumber === this.pharmacy.shelvesNumber) {
        this.payload = {
          ...this.payload
        };
      } else {
        this.payload = {
          ...this.payload,
          shelvesNumber: +data.shelvesNumber
        };
      }

      delete this.payload.customerSMSLanguages;

      await this.updatePharmacy({
        data: this.payload,
        pharmacyId: id
      });

      await this.getPharmacy(id);

      this.loading = false;
    },
    openModal() {
      if (!this.formData.serviceRequest) {
        this.showModalServiceRequest = true;
      }
    },
    async saveServiceRequest(data: DataServiceRequest) {
      this.loading = true;
      const { id } = this.$route.params;

      const payload = {
        serviceRequest: !data.serviceRequest,
        description: data.description
      };

      await this.getPharmacyServiceRequest({
        data: payload,
        pharmacyId: id
      });

      await this.getPharmacy(id);

      this.formData = {
        ...this.pharmacy
      };

      this.showModalServiceRequest = false;
      this.loading = false;
    },
    selectLanguage(option: Option) {
      this.langsDropdown.selectedOption = option;
    },
    addEmailItem() {
      if (this.user?.role.permissions.pharmacy.update) {
        this.emailItems.push(`email${token()}`);
      }
    },
    removeEmailItem(name: string) {
      this.emailItems = this.emailItems.filter((item: string) => item !== name);
    },
    addPhoneItem() {
      if (this.user?.role.permissions.pharmacy.update) {
        this.phoneItems.push(`phone${token()}`);
      }
    },
    removePhoneItem(name: string) {
      this.phoneItems = this.phoneItems.filter((item: string) => item !== name);
    },
    submitForm() {
      this.$formkit.submit('pharmacyForm');
    },
    cleanForm() {
      this.formData.name = '';
      this.formData.email = '';
      this.formData.additionalEmails = {};
      this.formData.phoneNumber = '';
      this.formData.additionalPhoneNumbers = {};
    }
  },
  watch: {
    'langsDropdown.selectedOption': {
      immediate: true,
      handler() {
        const languagesValues = this.langsDropdown.selectedOption.map(
          (i: Option) => i.value
        );

        this.formData.customerSMSLanguages = languagesValues || '';
      }
    },
    pharmacy() {
      if (this.pharmacy) {
        this.initLanguage();

        const languages = this.pharmacy.pickupBox.customerSMSLanguages;

        const languageMappings: { [key: string]: Record<string, unknown> } = {
          en: langsEn,
          fi: langsFi,
          sv: langsSv,
          no: langsNo
        };

        const userLanguage = this.user?.appLanguage;
        if (userLanguage && languageMappings[userLanguage]) {
          const selectedOption = languages.map((i: string) => ({
            label: languageMappings[userLanguage][i],
            value: i
          }));

          this.langsDropdown.selectedOption = selectedOption;
        }
      }
    }
  }
})
export default class EditPharmacy extends Vue {}
</script>

<style lang="scss">
.editPharmacy {
  padding: 24px;
  margin-bottom: 64px;
  overflow-y: scroll;
  .formkit-form {
    margin-top: 30px;
    .block-input {
      position: relative;
      span {
        position: absolute;
        top: 18px;
        left: 330px;
        font-weight: 400;
        font-size: $fourteenSize;
        line-height: $fourteenSize;
      }
    }
    .infoOfPharmacy-dropdown {
      height: 48px;
      width: 377px;
      margin-bottom: 30px;
    }
  }
  .closeInput {
    display: none;
  }
}
</style>
